import type { DateRange } from "react-day-picker";

import type { MapPosition } from "@locators/components/shared/hooks/useFlotiqStores/useFlotiqStores.types";

export type EventsFilterURLData = {
  mapPosition: MapPosition;
  dateRange: DateRange | undefined;
};

const DATE_FROM_KEY = "dateFrom";
const DATE_TO_KEY = "dateTo";

const pad = (n: number) => n.toString().padStart(2, "0");

const formatDateParam = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const parseDateParam = (value: string): Date | undefined => {
  const date = new Date(`${value}T00:00:00`);
  return isNaN(date.getTime()) ? undefined : date;
};

export const convertEventsFiltersToURLObj = (
  mapPosition?: MapPosition,
  dateRange?: DateRange
): Record<string, string> => {
  const urlParamsObj: Record<string, string> = {};

  if (mapPosition) {
    if (typeof mapPosition.lat === "number") urlParamsObj.lat = mapPosition.lat.toString();
    if (typeof mapPosition.lng === "number") urlParamsObj.lng = mapPosition.lng.toString();
    if (typeof mapPosition.zoom === "number") urlParamsObj.zoom = mapPosition.zoom.toString();
  }

  if (dateRange?.from) {
    urlParamsObj[DATE_FROM_KEY] = formatDateParam(dateRange.from);
    if (dateRange.to) {
      urlParamsObj[DATE_TO_KEY] = formatDateParam(dateRange.to);
    }
  }

  return urlParamsObj;
};

export const convertEventsFiltersFromURLParams = (urlParams: URLSearchParams): EventsFilterURLData => {
  const mapPosition: MapPosition = {};

  if (urlParams.has("lat")) {
    const lat = urlParams.get("lat");
    if (lat !== null) mapPosition.lat = parseFloat(lat);
  }
  if (urlParams.has("lng")) {
    const lng = urlParams.get("lng");
    if (lng !== null) mapPosition.lng = parseFloat(lng);
  }
  if (urlParams.has("zoom")) {
    const zoom = urlParams.get("zoom");
    if (zoom !== null) mapPosition.zoom = parseFloat(zoom);
  }

  let dateRange: DateRange | undefined;
  const dateFromStr = urlParams.get(DATE_FROM_KEY);

  if (dateFromStr) {
    const from = parseDateParam(dateFromStr);
    if (from) {
      const dateToStr = urlParams.get(DATE_TO_KEY);
      const to = dateToStr ? parseDateParam(dateToStr) : undefined;
      dateRange = { from, to };
    }
  }

  return { mapPosition, dateRange };
};

export const getInitialEventsDataFromURL = (): EventsFilterURLData => {
  if (typeof window !== "undefined") {
    const urlParams = new URLSearchParams(window.location.search);
    return convertEventsFiltersFromURLParams(urlParams);
  }

  return { mapPosition: {}, dateRange: undefined };
};
